import { EmailTemplate, TemplateType } from '../../../mocks/emails';
import { bookingsData } from '../../../mocks/bookings';
import { agencyInfo } from '../../../mocks/dashboard';

interface EmailPreviewProps {
  template: EmailTemplate;
  onClose: () => void;
  onUse: () => void;
}

const brand = agencyInfo.brandColor;

function BookingConfirmationPreview() {
  const booking = bookingsData[0];
  return (
    <div className="font-sans">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden max-w-xl mx-auto">
        {/* Header */}
        <div className="px-8 py-6 text-white" style={{ backgroundColor: brand }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center font-bold text-lg">
              {agencyInfo.logo}
            </div>
            <div>
              <p className="font-bold text-base">{agencyInfo.name}</p>
              <p className="text-xs opacity-80">Your Trusted Travel Partner</p>
            </div>
          </div>
          <div className="bg-white/15 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-checkbox-circle-fill text-green-300 text-xl" />
              <span className="font-semibold text-lg">Booking Confirmed!</span>
            </div>
            <p className="text-sm opacity-90">Reference: <strong>{booking.id}</strong></p>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <p className="text-gray-700 text-sm mb-4">
            Dear <strong>{booking.customer}</strong>,<br /><br />
            We are thrilled to confirm your booking! All arrangements have been made and your trip is all set.
            Please find your booking summary below.
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Booking Details</p>
            <div className="space-y-2">
              {[
                { label: 'Service', value: booking.service },
                { label: 'Destination', value: booking.destination },
                { label: 'Travel Date', value: booking.travelDate },
                { label: 'Return Date', value: booking.returnDate },
                { label: 'Passengers', value: `${booking.pax} person(s)` },
                { label: 'Total Amount', value: `${booking.currency} ${booking.totalAmount.toLocaleString()}` },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-800">{value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 mb-4">
            <p className="text-xs font-semibold text-amber-700 mb-1.5 flex items-center gap-1.5">
              <i className="ri-information-line" /> Important Reminders
            </p>
            <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
              <li>Please ensure all passports are valid for 6+ months</li>
              <li>Arrive at the airport at least 3 hours before departure</li>
              <li>Keep this confirmation email handy</li>
            </ul>
          </div>

          <p className="text-xs text-gray-500">
            For any queries, contact us at <strong>travel@horizontravel.ae</strong> or call <strong>+971 4 123 4567</strong>
          </p>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">{agencyInfo.name} &bull; Dubai, UAE &bull; IATA Accredited</p>
        </div>
      </div>
    </div>
  );
}

function InvoicePreview() {
  return (
    <div className="font-sans">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden max-w-xl mx-auto">
        <div className="px-8 py-5 flex justify-between items-start" style={{ backgroundColor: '#fffbeb' }}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: brand }}>
                {agencyInfo.logo}
              </div>
              <span className="font-bold text-gray-800">{agencyInfo.name}</span>
            </div>
            <p className="text-xs text-gray-500">Dubai, UAE &bull; IATA Accredited</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-amber-600 text-lg">INVOICE</p>
            <p className="text-xs text-gray-500">INV-2026-1084</p>
            <p className="text-xs text-gray-400 mt-1">Due: 30 Mar 2026</p>
          </div>
        </div>
        <div className="px-8 py-5">
          <div className="grid grid-cols-2 gap-4 mb-5 text-sm">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Bill To</p>
              <p className="font-medium text-gray-800">Ahmed Al-Rashid</p>
              <p className="text-xs text-gray-500">ahmed.rashid@email.com</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Issued</p>
              <p className="text-sm text-gray-700">20 Mar 2026</p>
            </div>
          </div>
          <table className="w-full text-xs border-collapse mb-4">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-2 px-3 text-gray-500 font-medium rounded-l-lg">Description</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium">Qty</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium rounded-r-lg">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-2.5 px-3 text-gray-700">Maldives Honeymoon Package (7N)</td>
                <td className="py-2.5 px-3 text-right text-gray-700">2</td>
                <td className="py-2.5 px-3 text-right text-gray-700">AED 14,000</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2.5 px-3 text-gray-700">Seaplane Transfer (Round Trip)</td>
                <td className="py-2.5 px-3 text-right text-gray-700">2</td>
                <td className="py-2.5 px-3 text-right text-gray-700">AED 1,000</td>
              </tr>
            </tbody>
          </table>
          <div className="bg-gray-50 rounded-xl p-3 text-xs space-y-1.5">
            <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>AED 15,000</span></div>
            <div className="flex justify-between text-gray-600"><span>VAT (5%)</span><span>AED 0</span></div>
            <div className="flex justify-between font-bold text-gray-800 text-sm pt-1.5 border-t border-gray-200 mt-1.5">
              <span>Total Due</span><span>AED 15,000</span>
            </div>
          </div>
        </div>
        <div className="px-8 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">{agencyInfo.name} &bull; Dubai, UAE</p>
        </div>
      </div>
    </div>
  );
}

function ItineraryPreview() {
  const days = [
    { day: 1, title: 'Arrival in Maldives', activities: ['Seaplane transfer to Soneva Fushi', 'Welcome dinner & sunset view'] },
    { day: 2, title: 'Underwater Exploration', activities: ['Snorkeling at coral reef', 'Private beach lunch', 'Spa treatment'] },
    { day: 3, title: 'Island Excursion', activities: ['Day trip to local island', 'Dolphin cruise at dusk'] },
  ];
  return (
    <div className="font-sans">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden max-w-xl mx-auto">
        <div className="px-8 py-6 text-white" style={{ backgroundColor: '#8b5cf6' }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center font-bold text-xs">{agencyInfo.logo}</div>
            <span className="font-bold">{agencyInfo.name}</span>
          </div>
          <p className="font-bold text-xl">Maldives Honeymoon</p>
          <p className="text-sm opacity-80 mt-1">Apr 15 \u2013 Apr 22, 2026 &bull; 7 Nights &bull; 2 Guests</p>
        </div>
        <div className="px-8 py-5">
          <p className="text-sm text-gray-600 mb-4">Dear Ahmed, here is your personalized day-by-day itinerary. Everything has been arranged for a magical experience!</p>
          {days.map((d) => (
            <div key={d.day} className="flex gap-3 mb-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: '#8b5cf6' }}>
                {d.day}
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm">{d.title}</p>
                {d.activities.map((a) => (
                  <p key={a} className="text-xs text-gray-500 mt-0.5 flex items-start gap-1">
                    <i className="ri-checkbox-circle-line text-purple-400 mt-0.5 flex-shrink-0" />{a}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-8 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">{agencyInfo.name} &bull; Your Trusted Travel Partner</p>
        </div>
      </div>
    </div>
  );
}

function PaymentReceiptPreview() {
  return (
    <div className="font-sans">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden max-w-xl mx-auto">
        <div className="px-8 py-5 text-white" style={{ backgroundColor: '#06b6d4' }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center font-bold text-xs">{agencyInfo.logo}</div>
            <span className="font-bold">{agencyInfo.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-shield-check-line text-2xl" />
            </div>
            <div>
              <p className="font-bold text-lg">Payment Received</p>
              <p className="text-sm opacity-80">Thank you for your payment!</p>
            </div>
          </div>
        </div>
        <div className="px-8 py-5">
          <div className="text-center mb-5">
            <p className="text-3xl font-bold text-gray-800">AED 7,500</p>
            <p className="text-sm text-gray-400 mt-1">Received on 20 Mar 2026</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2">
            {[
              { label: 'Payment Method', value: 'Bank Transfer' },
              { label: 'Reference', value: 'TRF-20032026-001' },
              { label: 'Booking', value: 'BK-1084 \u2014 Maldives Honeymoon' },
              { label: 'Balance Due', value: 'AED 7,500' },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between">
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-800">{value}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-cyan-50 border border-cyan-200 rounded-xl text-xs text-cyan-700">
            <i className="ri-information-line mr-1" />
            Remaining balance of AED 7,500 is due by 30 Mar 2026
          </div>
        </div>
        <div className="px-8 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">{agencyInfo.name} &bull; Dubai, UAE</p>
        </div>
      </div>
    </div>
  );
}

function GenericPreview({ template }: { template: EmailTemplate }) {
  return (
    <div className="font-sans">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden max-w-xl mx-auto">
        <div className="px-8 py-5 text-white" style={{ backgroundColor: template.color }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center font-bold text-xs">{agencyInfo.logo}</div>
            <span className="font-bold">{agencyInfo.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <i className={`${template.icon} text-3xl`} />
            <p className="font-bold text-lg">{template.name}</p>
          </div>
        </div>
        <div className="px-8 py-5">
          <p className="text-sm text-gray-600 mb-4">{template.description}</p>
          <div className="bg-gray-50 rounded-xl p-4 border border-dashed border-gray-200 text-center">
            <i className="ri-edit-2-line text-2xl text-gray-300 mb-2 block" />
            <p className="text-sm text-gray-400">Customize this template to fit your needs</p>
          </div>
        </div>
        <div className="px-8 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">{agencyInfo.name} &bull; Dubai, UAE</p>
        </div>
      </div>
    </div>
  );
}

const previewMap: Partial<Record<TemplateType, () => JSX.Element>> = {
  booking_confirmation: BookingConfirmationPreview,
  invoice: InvoicePreview,
  itinerary: ItineraryPreview,
  payment_receipt: PaymentReceiptPreview,
};

export default function EmailPreview({ template, onClose, onUse }: EmailPreviewProps) {
  const Preview = previewMap[template.type];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-50 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: template.bgColor, color: template.color }}
            >
              <i className={`${template.icon} text-base`} />
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">{template.name}</p>
              <p className="text-xs text-gray-400">Email Preview</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Preview area */}
        <div className="flex-1 overflow-y-auto p-6">
          {Preview ? <Preview /> : <GenericPreview template={template} />}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white rounded-b-2xl flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
          <button
            onClick={onUse}
            className="px-4 py-2 text-sm rounded-lg text-white font-medium cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: template.color }}
          >
            Use This Template
          </button>
        </div>
      </div>
    </div>
  );
}
